import { useNavigate } from 'react-router-dom';
import './Legal.css';

const LAST_UPDATED = '26 aprile 2025';
const EFFECTIVE_DATE = '26 aprile 2025';
const CONTACT_EMAIL = 'alex.siroli@gmail.com';
const APP_NAME = 'Chain Reaction';
const DEVELOPER_NAME = 'Alex Siroli';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <button className="legal-header__back" onClick={() => navigate(-1)}>
          ← Indietro
        </button>
        <h1 className="legal-header__title">Privacy Policy</h1>
        <button className="legal-header__sibling-link" onClick={() => navigate('/terms')}>
          Termini di Servizio →
        </button>
      </header>

      {/* Content */}
      <main className="legal-content">
        {/* Hero */}
        <div className="legal-hero">
          <div className="legal-badge">🔒 Privacy & Dati</div>
          <h2 className="legal-hero__title">Informativa sulla Privacy</h2>
          <p className="legal-hero__meta">
            <span>📅 Ultimo aggiornamento: {LAST_UPDATED}</span>
            <span>✅ In vigore dal: {EFFECTIVE_DATE}</span>
          </p>
        </div>

        {/* Intro */}
        <div className="legal-section">
          <div className="legal-highlight legal-highlight--info">
            <p>
              <strong>Versione semplice:</strong> {APP_NAME} raccoglie solo i dati strettamente
              necessari per farti giocare e salvare i tuoi progressi. Non vendiamo i tuoi dati, non
              mostriamo pubblicità, non tracciamo la tua navigazione.
            </p>
          </div>

          <p>
            {DEVELOPER_NAME} ("<strong>noi</strong>", "<strong>il titolare</strong>") è il titolare
            del trattamento dei dati personali raccolti tramite l'applicazione {APP_NAME}
            (disponibile su web, Google Play Store e Apple App Store). La presente Informativa
            descrive quali dati raccogliamo, come li utilizziamo e quali sono i tuoi diritti ai
            sensi del Regolamento (UE) 2016/679 (GDPR) e delle normative applicabili.
          </p>
        </div>

        {/* 1 — Titolare */}
        <div className="legal-section">
          <h3 className="legal-section__title">1. Titolare del Trattamento</h3>
          <p>
            <strong>{DEVELOPER_NAME}</strong>
            <br />
            Contatto privacy: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
          <p>
            Per qualsiasi richiesta relativa ai tuoi dati personali puoi scriverci all'indirizzo
            sopra indicato. Risponderemo entro 30 giorni.
          </p>
        </div>

        {/* 2 — Dati Raccolti */}
        <div className="legal-section">
          <h3 className="legal-section__title">2. Dati che Raccogliamo</h3>
          <p>
            Raccogliamo soltanto le informazioni indispensabili al funzionamento dell'app. La
            tabella seguente descrive in modo trasparente ogni categoria:
          </p>

          <div className="legal-table-wrapper">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Dato specifico</th>
                  <th>Finalità</th>
                  <th>Base giuridica</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Identità account</strong>
                  </td>
                  <td>Nome, foto profilo, indirizzo e-mail (forniti da Google)</td>
                  <td>Autenticazione, visualizzazione profilo</td>
                  <td>Contratto (esecuzione del servizio)</td>
                </tr>
                <tr>
                  <td>
                    <strong>Identificativo utente</strong>
                  </td>
                  <td>UID Firebase univoco</td>
                  <td>Collegare le statistiche all'account</td>
                  <td>Contratto</td>
                </tr>
                <tr>
                  <td>
                    <strong>Statistiche di gioco</strong>
                  </td>
                  <td>
                    Punteggi, parole indovinate, errori, pass usati, cronologia partite, modalità di
                    gioco
                  </td>
                  <td>Mostrare i progressi nel profilo personale</td>
                  <td>Contratto / Legittimo interesse</td>
                </tr>
                <tr>
                  <td>
                    <strong>Dati squadre</strong>
                  </td>
                  <td>Nome squadra, codice invito, membri e ruoli</td>
                  <td>Funzionalità multiplayer e squadre</td>
                  <td>Contratto</td>
                </tr>
                <tr>
                  <td>
                    <strong>Dati tecnici locali</strong>
                  </td>
                  <td>Preferenze tema (chiaro/scuro), dimensione carattere</td>
                  <td>Memorizzare le preferenze estetiche dell'utente</td>
                  <td>Legittimo interesse</td>
                </tr>
                <tr>
                  <td>
                    <strong>Parametri tecnici sessione</strong>
                  </td>
                  <td>Orientamento schermo, tipo di dispositivo</td>
                  <td>Adattare l'interfaccia graficamente in tempo reale</td>
                  <td>Legittimo interesse</td>
                </tr>
                <tr>
                  <td>
                    <strong>Dati di sessione Firebase</strong>
                  </td>
                  <td>Token di sessione cifrati gestiti da Firebase Authentication</td>
                  <td>Sicurezza e persistenza del login</td>
                  <td>Contratto</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="legal-highlight legal-highlight--warn">
            <p>
              <strong>Non raccogliamo:</strong> dati di pagamento, posizione geografica, contatti
              del telefono, identificatori pubblicitari (IDFA/GAID), dati biometrici né qualsiasi
              informazione non indicata sopra.
            </p>
          </div>
        </div>

        {/* 3 — Modalità di Raccolta */}
        <div className="legal-section">
          <h3 className="legal-section__title">3. Come Raccogliamo i Dati</h3>
          <ul>
            <li>
              <strong>Direttamente da te:</strong> quando accedi con il tuo account Google tramite
              Firebase Authentication, quando giochi e quando configuri le preferenze dell'app.
            </li>
            <li>
              <strong>Automaticamente:</strong> Firebase Firestore salva automaticamente le
              statistiche di gioco associate al tuo UID al termine di ogni partita.
            </li>
            <li>
              <strong>Da terze parti:</strong> Google LLC fornisce i dati del tuo profilo (nome,
              e-mail, foto) tramite OAuth 2.0 nel momento dell'accesso.
            </li>
          </ul>
        </div>

        {/* 4 — Condivisione */}
        <div className="legal-section">
          <h3 className="legal-section__title">4. Con Chi Condividiamo i Dati</h3>
          <p>
            <strong>Non vendiamo né affittiamo i tuoi dati personali a terzi.</strong> I dati
            vengono condivisi solo con i seguenti sub-responsabili del trattamento, strettamente
            necessari al funzionamento dell'app:
          </p>
          <ul>
            <li>
              <strong>Google LLC / Firebase</strong> — fornisce autenticazione (Firebase
              Authentication) e database cloud (Firestore). Dati ospitati su server in Europa
              (eur3). Privacy Policy:{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                policies.google.com/privacy
              </a>
            </li>
            <li>
              <strong>Netlify, Inc.</strong> — hosting della web app e distribuzione CDN. Privacy
              Policy:{' '}
              <a href="https://www.netlify.com/privacy/" target="_blank" rel="noreferrer">
                netlify.com/privacy
              </a>
            </li>
          </ul>
          <p>
            Tutti i fornitori adottano misure di sicurezza adeguate e sono conformi al GDPR
            attraverso le rispettive Clausole Contrattuali Standard (SCC).
          </p>
        </div>

        {/* 5 — Trasferimenti */}
        <div className="legal-section">
          <h3 className="legal-section__title">5. Trasferimento dei Dati al di Fuori dell'UE</h3>
          <p>
            Firebase e Netlify hanno sede negli Stati Uniti. Il trasferimento dei dati avviene nel
            rispetto delle Clausole Contrattuali Standard approvate dalla Commissione Europea (SCC,
            ai sensi dell'art. 46 GDPR) e, ove applicabile, del Data Privacy Framework UE-USA.
          </p>
        </div>

        {/* 6 — Conservazione */}
        <div className="legal-section">
          <h3 className="legal-section__title">6. Conservazione dei Dati</h3>
          <div className="legal-table-wrapper">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Tipo di dato</th>
                  <th>Periodo di conservazione</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Dati account e statistiche di gioco</td>
                  <td>
                    Fino alla cancellazione dell'account da parte dell'utente o per inattività
                    superiore a 24 mesi
                  </td>
                </tr>
                <tr>
                  <td>Dati delle sessioni multiplayer/lobby</td>
                  <td>
                    Eliminati quando l'ultimo giocatore abbandona la stanza o dopo un periodo di
                    inattività
                  </td>
                </tr>
                <tr>
                  <td>Preferenze locali (tema, font)</td>
                  <td>
                    Memorizzate nel localStorage del dispositivo; cancellate alla disconnessione o
                    alla cancellazione dati del browser/app
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 7 — Diritti GDPR */}
        <div className="legal-section">
          <h3 className="legal-section__title">7. I Tuoi Diritti (GDPR)</h3>
          <p>In qualità di interessato, hai il diritto di:</p>
          <ul>
            <li>
              <strong>Accesso (art. 15):</strong> ottenere copia dei tuoi dati personali.
            </li>
            <li>
              <strong>Rettifica (art. 16):</strong> correggere dati inesatti.
            </li>
            <li>
              <strong>Cancellazione (art. 17):</strong> richiedere la rimozione dei tuoi dati
              ("diritto all'oblio").
            </li>
            <li>
              <strong>Limitazione (art. 18):</strong> richiedere la limitazione del trattamento.
            </li>
            <li>
              <strong>Portabilità (art. 20):</strong> ricevere i tuoi dati in formato strutturato.
            </li>
            <li>
              <strong>Opposizione (art. 21):</strong> opporti al trattamento basato su legittimo
              interesse.
            </li>
            <li>
              <strong>Revoca del consenso:</strong> ove il consenso fosse la base giuridica,
              revocarlo in qualsiasi momento.
            </li>
          </ul>
          <p>
            Per esercitare i tuoi diritti scrivi a{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Hai inoltre il diritto di
            proporre reclamo all'
            <strong>Autorità Garante per la Protezione dei Dati Personali</strong> (Garante Privacy,{' '}
            <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer">
              garanteprivacy.it
            </a>
            ).
          </p>

          <div className="legal-highlight">
            <p>
              <strong>Come cancellare il tuo account:</strong> Scrivi a{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> con oggetto "Richiesta
              cancellazione account" indicando l'e-mail usata per accedere. Elaboreremo la richiesta
              entro 30 giorni, cancellando tutti i dati associati al tuo UID da Firestore.
            </p>
          </div>
        </div>

        {/* 8 — Sicurezza */}
        <div className="legal-section">
          <h3 className="legal-section__title">8. Sicurezza dei Dati</h3>
          <p>Adottiamo misure tecniche e organizzative adeguate per proteggere i tuoi dati:</p>
          <ul>
            <li>
              Tutte le comunicazioni sono cifrate tramite <strong>TLS/HTTPS</strong>.
            </li>
            <li>
              L'accesso al database Firestore è protetto da <strong>regole di sicurezza</strong>
              che impediscono a qualsiasi utente di leggere i dati altrui.
            </li>
            <li>
              Le credenziali di autenticazione sono gestite interamente da{' '}
              <strong>Firebase Authentication</strong>e non vengono mai memorizzate sui nostri
              server.
            </li>
            <li>
              Le chiavi di configurazione Firebase sono pubbliche per design (protezione avviene
              tramite regole Firestore e Firebase Security Rules, non tramite segretezza delle
              chiavi).
            </li>
          </ul>
        </div>

        {/* 9 — Minori */}
        <div className="legal-section">
          <h3 className="legal-section__title">9. Minori (Children's Privacy)</h3>
          <p>
            {APP_NAME} è destinata a utenti di <strong>almeno 13 anni</strong> (o l'età minima
            consentita nella propria giurisdizione). Non raccogliamo consapevolmente dati di bambini
            al di sotto di tale soglia. Se ritieni che un minore abbia creato un account, contattaci
            immediatamente a <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> e provvederemo
            alla cancellazione del profilo entro 72 ore.
          </p>
          <div className="legal-highlight legal-highlight--warn">
            <p>
              <strong>Nota per i genitori:</strong> {APP_NAME} non contiene pubblicità, acquisti
              in-app, chat in tempo reale con estranei né contenuti inappropriati. Il gioco è adatto
              a tutta la famiglia nella pratica, ma richiediamo un account Google per salvare le
              statistiche, motivo per cui consigliamo supervisione genitoriale per i minori di 13
              anni.
            </p>
          </div>
        </div>

        {/* 10 — Cookie e Tracking */}
        <div className="legal-section">
          <h3 className="legal-section__title">10. Cookie e Tecnologie di Tracciamento</h3>
          <p>
            L'app non utilizza cookie di profilazione, tracciamento pubblicitario o analytics di
            terze parti. Utilizziamo esclusivamente:
          </p>
          <ul>
            <li>
              <strong>localStorage</strong> (memorizzazione locale nel dispositivo) per salvare
              preferenze come tema e dimensione del testo. Questi dati vengono rimossi dal
              dispositivo al momento della disconnessione (logout).
            </li>
            <li>
              <strong>Parametri di visualizzazione</strong> (non persistenti) come l'orientamento
              dello schermo, utilizzati solo per adattare il layout durante l'uso.
            </li>
            <li>
              <strong>Session token Firebase</strong> (tecnici, strettamente necessari) per
              mantenere la sessione di accesso.
            </li>
          </ul>
          <p>
            <strong>Firebase Analytics è disabilitato</strong> nell'attuale versione dell'app (il
            relativo codice è commentato nel sorgente).
          </p>
        </div>

        {/* 11 — Modifiche */}
        <div className="legal-section">
          <h3 className="legal-section__title">11. Modifiche a questa Informativa</h3>
          <p>
            Potremmo aggiornare periodicamente la presente Informativa. In caso di modifiche
            sostanziali, ti informeremo tramite una notifica in-app o via e-mail almeno 15 giorni
            prima dell'entrata in vigore. La versione vigente è sempre disponibile all'indirizzo
            <strong> /privacy</strong> all'interno dell'app. L'uso continuato del servizio dopo
            l'entrata in vigore costituisce accettazione delle modifiche.
          </p>
        </div>

        {/* 12 — Legge applicabile */}
        <div className="legal-section">
          <h3 className="legal-section__title">12. Legge Applicabile e Foro Competente</h3>
          <p>
            La presente Informativa è disciplinata dalla legge italiana e dal Regolamento (UE)
            2016/679 (GDPR). Per qualsiasi controversia relativa al trattamento dei dati personali,
            salvo diverso accordo scritto, è competente il Foro di Bologna, Italia.
          </p>
        </div>

        {/* Footer */}
        <div className="legal-footer">
          <p>
            © {new Date().getFullYear()} {DEVELOPER_NAME} — {APP_NAME}
          </p>
          <p>Versione documento: 1.0 — {EFFECTIVE_DATE}</p>
          <div className="legal-footer__links">
            <button onClick={() => navigate('/terms')}>Termini di Servizio</button>
            <a href={`mailto:${CONTACT_EMAIL}`}>Contattaci</a>
            <button onClick={() => navigate('/')}>Torna alla Home</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
